import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearContactMessageDetail,
  fetchContactMessageById,
  removeContactMessage,
  rerunContactMessageClientEmailAnalysis,
  sendContactMessageEmail,
  sendContactMessageReply,
  updateContactMessage,
} from "../../store/slices/contactMessagesSlice";
import { AdminTypedDeleteModal } from "./AdminConfirmModals";
import AdminCollapsiblePanel from "./AdminCollapsiblePanel";
import { AdminEmailThreadPanel } from "./AdminEmailThreadPanel";
import AdminStatusWorkflow from "./AdminStatusWorkflow";
import { adminStatusBadgeClass, adminStatusLabel, canonicalAdminStatus } from "./adminStatusUi";

function empty(s) {
  const t = (s || "").trim();
  return t || "—";
}

function rowToForm(row) {
  const msg = row.message || row.body || "";
  return {
    fullName: row.fullName || row.name || "",
    email: row.email || "",
    phone: row.phone || "",
    country: row.country || "",
    company: row.company || "",
    message: msg,
    newsletter: Boolean(row.newsletter),
    status: canonicalAdminStatus(row.status),
  };
}

export default function AdminContactEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiOn = isAdminApiEnabled();
  const dispatch = useAppDispatch();
  const detail = useAppSelector((s) => s.contactMessages.detail);
  const detailStatus = useAppSelector((s) => s.contactMessages.detailStatus);
  const detailError = useAppSelector((s) => s.contactMessages.detailError);
  const seenMarkedRef = useRef(null);

  const row = apiOn ? detail : null;

  const [form, setForm] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const clientEmailOutbound = useAppSelector((s) => s.contactMessages.clientEmailOutbound);
  const clientEmailSendHttpPending = useAppSelector(
    (s) => s.contactMessages.clientEmailSendHttpPending
  );
  const [editingFields, setEditingFields] = useState(false);
  const [deleteMessageOpen, setDeleteMessageOpen] = useState(false);

  useEffect(() => {
    if (!apiOn) return undefined;
    return () => {
      dispatch(clearContactMessageDetail());
    };
  }, [apiOn, dispatch]);

  useEffect(() => {
    if (!apiOn || !id) return;
    seenMarkedRef.current = null;
    dispatch(clearContactMessageDetail());
    dispatch(fetchContactMessageById(id));
  }, [apiOn, id, dispatch]);

  useEffect(() => {
    if (!apiOn || !id || !row || row.id !== id) return;
    if (seenMarkedRef.current === id) return;
    seenMarkedRef.current = id;
    if (!row.seenAt) {
      dispatch(
        updateContactMessage({
          id,
          body: { seenAt: new Date().toISOString() },
        })
      );
    }
  }, [apiOn, dispatch, id, row]);

  useEffect(() => {
    if (!row) {
      setForm(null);
      return;
    }
    if (editingFields) return;
    setForm(rowToForm(row));
  }, [row, editingFields]);

  const handleField = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) =>
      prev ? { ...prev, [name]: type === "checkbox" ? checked : value } : prev
    );
  }, []);

  const setPipelineStatus = useCallback((status) => {
    setForm((prev) => (prev ? { ...prev, status } : prev));
  }, []);

  const handleApplyStatus = useCallback(() => {
    if (!id || !form) return;
    dispatch(updateContactMessage({ id, body: { status: form.status } }));
  }, [dispatch, form, id]);

  const handleSaveFields = useCallback(
    (e) => {
      e.preventDefault();
      if (!id || !form) return;
      const { fullName, email, phone, country, company, message, newsletter } = form;
      dispatch(
        updateContactMessage({
          id,
          body: {
            fullName,
            name: fullName,
            email,
            phone,
            country,
            company,
            message,
            body: message,
            newsletter,
          },
        })
      );
      setEditingFields(false);
    },
    [dispatch, form, id]
  );

  const handleCancelEdit = useCallback(() => {
    if (row) setForm(rowToForm(row));
    setEditingFields(false);
  }, [row]);

  const handleReply = useCallback(
    (e) => {
      e.preventDefault();
      if (!id || !replyBody.trim()) return;
      dispatch(
        sendContactMessageReply({
          id,
          body: replyBody.trim(),
          internal: true,
        })
      );
      setReplyBody("");
    },
    [dispatch, id, replyBody]
  );

  const handleSendClientEmail = useCallback(
    async (payload) => {
      if (!id) return;
      try {
        await dispatch(sendContactMessageEmail({ id, ...payload })).unwrap();
      } catch {
        /* toast from thunk */
      }
    },
    [dispatch, id]
  );

  const emailSending =
    (clientEmailOutbound && String(clientEmailOutbound.parentId) === String(id)) ||
    (Boolean(clientEmailSendHttpPending) && detail?.id != null && String(detail.id) === String(id));

  const handleRerunClientEmailAnalysis = useCallback(
    async (emailMessageId) => {
      if (!id) return;
      await dispatch(rerunContactMessageClientEmailAnalysis({ id, emailMessageId })).unwrap();
    },
    [dispatch, id]
  );

  const confirmDeleteMessage = useCallback(() => {
    if (!id) return;
    dispatch(removeContactMessage(id))
      .unwrap()
      .then(() => navigate("/admin/dashboard/contact"))
      .catch(() => {});
  }, [dispatch, id, navigate]);

  if (!apiOn) {
    return (
      <div className="admin-dash__page admin-dash__page--detail">
        <Link className="admin-dash__back" to="/admin/dashboard/contact">
          ← Contact messages
        </Link>
        <h1 className="admin-dash__h1" style={{ marginTop: 16 }}>
          Admin API is off
        </h1>
        <p className="admin-dash__panel-sub" style={{ maxWidth: 520 }}>
          Enable the admin API for this build and sign in to load and edit contact messages from your backend.
        </p>
      </div>
    );
  }

  if (id && (detailStatus === "idle" || detailStatus === "loading")) {
    return (
      <div className="admin-dash__page admin-dash__page--detail">
        <Link className="admin-dash__back" to="/admin/dashboard/contact">
          ← Contact messages
        </Link>
        <div className="admin-dash__load-banner" role="status" style={{ marginTop: 16 }}>
          <span className="admin-dash__load-banner-dot" aria-hidden />
          Loading message…
        </div>
        <div className="admin-dash__overview-skeleton" style={{ marginTop: 24 }}>
          <div className="admin-dash__sk admin-dash__overview-skeleton-banner admin-dash__sk--pulse" />
          <div className="admin-dash__sk admin-dash__overview-skeleton-panel admin-dash__sk--pulse" />
        </div>
      </div>
    );
  }

  if (detailStatus === "failed") {
    return (
      <div className="admin-dash__page">
        <p className="admin-dash__empty-state-title">Could not load this message</p>
        <p className="admin-dash__td-muted" style={{ marginTop: 8 }}>
          {detailError || "Unknown error"}
        </p>
        <Link className="admin-dash__link" to="/admin/dashboard/contact" style={{ marginTop: 16, display: "inline-block" }}>
          ← Back to list
        </Link>
      </div>
    );
  }

  if (!row || !form) {
    return (
      <div className="admin-dash__page">
        <p className="admin-dash__empty">Message not found.</p>
        <Link className="admin-dash__link" to="/admin/dashboard/contact">
          ← Back to list
        </Link>
      </div>
    );
  }

  const replies = row.replies || [];
  const emailMessages = row.emailMessages ?? row.emailThread ?? [];
  const savedStatus = canonicalAdminStatus(row.status);
  const statusDirty = form.status !== savedStatus;
  const defaultEmailSubject = `Re: Your message — ${(form.fullName || "").trim() || "VizHacks"}`;

  return (
    <div className="admin-dash__page admin-dash__page--detail">
      <header className="admin-dash__page-head">
        <Link className="admin-dash__back" to="/admin/dashboard/contact">
          ← Contact messages
        </Link>
        <p className="admin-dash__page-eyebrow">Contact messages</p>
        <h1 className="admin-dash__h1">{empty(form.fullName)}</h1>
        <p className="admin-dash__detail-meta">
          <time dateTime={row.createdAt}>{new Date(row.createdAt).toLocaleString()}</time>
          {row._demo ? <span className="admin-dash__tag-muted">sample</span> : null}
        </p>
        <div className="admin-dash__meta-row admin-dash__meta-row--badges">
          <span className="admin-dash__badge-type">Contact</span>
          <span className={adminStatusBadgeClass(form.status)}>
            {adminStatusLabel(form.status)}
          </span>
          {form.newsletter ? (
            <span className="admin-dash__badge-newsletter">Newsletter opt-in</span>
          ) : (
            <span className="admin-dash__badge-muted">No newsletter</span>
          )}
        </div>
      </header>

      <section className="admin-dash__panel admin-dash__panel--workflow">
        <h2 className="admin-dash__h2">Client pipeline</h2>
        <p className="admin-dash__panel-sub admin-dash__panel-sub--inline">
          Same stages as service requests: new, waiting on the client after an offer, confirmed, or
          rejected. Choose the stage, then Apply. Message content stays read-only unless you choose
          Edit.
        </p>
        <div className="admin-dash__workflow-field admin-dash__workflow-field--rich">
          <span className="admin-dash__label admin-dash__label--workflow" id="con-pipeline-label">
            Stage
          </span>
          <div className="admin-dash__workflow-controls admin-dash__workflow-controls--stack">
            <AdminStatusWorkflow
              idPrefix="con-pipeline"
              ariaLabelledBy="con-pipeline-label"
              value={form.status}
              onChange={setPipelineStatus}
            />
            <div className="admin-dash__workflow-apply-row">
              <button
                type="button"
                className="admin-dash__btn-primary admin-dash__btn-primary--compact"
                disabled={!statusDirty}
                onClick={handleApplyStatus}
              >
                Apply stage
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-dash__panel">
        <div className="admin-dash__panel-head">
          <h2 className="admin-dash__h2">Message &amp; lead</h2>
          {!editingFields ? (
            <button
              type="button"
              className="admin-dash__btn-text"
              onClick={() => setEditingFields(true)}
            >
              Edit fields
            </button>
          ) : null}
        </div>

        {!editingFields ? (
          <dl className="admin-dash__detail-grid">
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Email</dt>
              <dd className="admin-dash__detail-value">
                {form.email.trim() ? (
                  <a className="admin-dash__link" href={`mailto:${form.email.trim()}`}>
                    {form.email.trim()}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Phone</dt>
              <dd className="admin-dash__detail-value">{empty(form.phone)}</dd>
            </div>
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Country</dt>
              <dd className="admin-dash__detail-value">{empty(form.country)}</dd>
            </div>
            <div className="admin-dash__detail-item">
              <dt className="admin-dash__detail-label">Company</dt>
              <dd className="admin-dash__detail-value">{empty(form.company)}</dd>
            </div>
            <div className="admin-dash__detail-item admin-dash__detail-item--full">
              <dt className="admin-dash__detail-label">Message</dt>
              <dd className="admin-dash__detail-value admin-dash__detail-value--multiline">
                {empty(form.message)}
              </dd>
            </div>
          </dl>
        ) : (
          <form className="admin-dash__form admin-dash__form--edit" onSubmit={handleSaveFields}>
            <label className="admin-dash__label">
              Full name
              <input
                className="admin-dash__input"
                name="fullName"
                value={form.fullName}
                onChange={handleField}
                required
              />
            </label>
            <label className="admin-dash__label">
              Email
              <input
                className="admin-dash__input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleField}
                required
              />
            </label>
            <label className="admin-dash__label">
              Phone
              <input className="admin-dash__input" name="phone" value={form.phone} onChange={handleField} />
            </label>
            <label className="admin-dash__label">
              Country
              <input className="admin-dash__input" name="country" value={form.country} onChange={handleField} />
            </label>
            <label className="admin-dash__label">
              Company
              <input className="admin-dash__input" name="company" value={form.company} onChange={handleField} />
            </label>
            <label className="admin-dash__label">
              Message
              <textarea
                className="admin-dash__textarea"
                name="message"
                value={form.message}
                onChange={handleField}
                rows={8}
              />
            </label>
            <label className="admin-dash__label admin-dash__label--checkbox">
              <input
                type="checkbox"
                name="newsletter"
                checked={form.newsletter}
                onChange={handleField}
              />
              Newsletter opt-in
            </label>
            <div className="admin-dash__form-actions">
              <button type="submit" className="admin-dash__btn-primary">
                Save changes
              </button>
              <button type="button" className="admin-dash__btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      <AdminCollapsiblePanel
        title="Internal replies"
        subtitle="Team-only — not emailed to the contact unless your backend sends mail"
        defaultOpen
        panelClassName="admin-dash__panel--collapsible-thread"
        badge={
          replies.length > 0 ? (
            <span className="admin-dash__collapsible-count-badge">{replies.length}</span>
          ) : null
        }
      >
        {replies.length === 0 ? (
          <p className="admin-dash__td-muted">No replies yet.</p>
        ) : (
          <ul className="admin-dash__reply-list">
            {replies.map((rep) => (
              <li key={rep.id} className="admin-dash__reply">
                <div className="admin-dash__reply-head">
                  <div className="admin-dash__reply-meta">
                    <strong>{rep.author}</strong>
                    <time dateTime={rep.createdAt}>
                      {new Date(rep.createdAt).toLocaleString()}
                    </time>
                  </div>
                </div>
                <p className="admin-dash__reply-body">{rep.body}</p>
              </li>
            ))}
          </ul>
        )}
        <form className="admin-dash__reply-form" onSubmit={handleReply}>
          <label className="admin-dash__label">
            Add reply
            <textarea
              className="admin-dash__textarea"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={3}
              placeholder="Internal note or draft response…"
            />
          </label>
          <button type="submit" className="admin-dash__btn-primary" disabled={!replyBody.trim()}>
            Post reply
          </button>
        </form>
      </AdminCollapsiblePanel>

      <AdminEmailThreadPanel
        emailMessages={emailMessages}
        clientEmail={form.email}
        defaultSubject={defaultEmailSubject}
        onSendEmail={handleSendClientEmail}
        onRerunClientAnalysis={handleRerunClientEmailAnalysis}
        sending={emailSending}
      />

      <section className="admin-dash__danger-panel" aria-labelledby="con-delete-heading">
        <h2 id="con-delete-heading" className="admin-dash__h2 admin-dash__h2--danger">
          Delete message
        </h2>
        <p className="admin-dash__thread-note admin-dash__thread-note--danger">
          Permanently remove this message from the server. This cannot be undone.
        </p>
        <button type="button" className="admin-dash__btn-danger" onClick={() => setDeleteMessageOpen(true)}>
          Delete message
        </button>
      </section>

      <AdminTypedDeleteModal
        open={deleteMessageOpen}
        onClose={() => setDeleteMessageOpen(false)}
        title="Delete this contact message?"
        description="This removes the message from the API. Replies are removed with it. This cannot be undone."
        phrase="Delete"
        confirmButtonLabel="Delete message"
        onConfirm={confirmDeleteMessage}
      />
    </div>
  );
}
